import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass, Funnel, UserCirclePlus, Eye, UserList, AirplaneTilt, Anchor } from '@phosphor-icons/react';

const API = '/api';

export default function Investigations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canAssign = user?.role === 'admin' || user?.role === 'iic';

  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [selectedInv, setSelectedInv] = useState(null);
  const [investigators, setInvestigators] = useState([]);
  const [loadingInvestigators, setLoadingInvestigators] = useState(false);

  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailInv, setDetailInv] = useState(null);

  useEffect(() => { loadInvestigations(); }, []);

  const loadInvestigations = async () => {
    try {
      const { data } = await axios.get(`${API}/investigations`, { withCredentials: true });
      setInvestigations(data);
    } catch (error) {
      console.error('Failed to load investigations:', error);
    } finally {
      setLoading(false);
    }
  };

  const openAssignDialog = async (inv) => {
    setSelectedInv(inv);
    setIsAssignOpen(true);
    setLoadingInvestigators(true);
    try {
      const { data } = await axios.get(`${API}/investigations/investigators/availability?sector=${inv.sector}`, { withCredentials: true });
      setInvestigators(data);
    } catch (error) {
      if (error.response?.status === 401) {
        setIsAssignOpen(false);
      } else {
        setInvestigators([]);
      }
    } finally {
      setLoadingInvestigators(false);
    }
  };

  const handleAssign = async (email) => {
    try {
      await axios.put(`${API}/investigations/${selectedInv.id}/assign`, {
        investigator_email: email
      }, { withCredentials: true });
      setIsAssignOpen(false);
      // Navigate IIC to investigation detail to generate ICAO 9756 tasks
      navigate(`/investigations/${selectedInv.id}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error assigning investigator');
    }
  };

  const openDetail = (inv) => {
    setDetailInv(inv);
    setIsDetailOpen(true);
  };

  const filteredInvestigations = useMemo(() => investigations.filter(inv => {
    const matchesSearch =
      inv.investigation_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.incident_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.lead_investigator || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || inv.status === filterStatus;
    return matchesSearch && matchesStatus;
  }), [investigations, searchTerm, filterStatus]);

  const summaryCounts = useMemo(() => ({
    total: investigations.length,
    initiated: investigations.filter(i => i.status === 'initiated').length,
    active: investigations.filter(i => !['completed', 'closed', 'initiated'].includes(i.status)).length,
    completed: investigations.filter(i => i.status === 'completed').length,
  }), [investigations]);

  const getStatusStyle = (status) => {
    const map = {
      initiated: 'bg-amber-100 text-amber-800',
      team_assignment: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-purple-100 text-purple-800',
      synthesis: 'bg-indigo-100 text-indigo-800',
      report_draft: 'bg-cyan-100 text-cyan-800',
      completed: 'bg-green-100 text-green-800',
      closed: 'bg-slate-200 text-slate-700'
    };
    return map[status] || 'bg-slate-100 text-slate-800';
  };

  const getStatusLabel = (status) => {
    const map = {
      initiated: 'Initiated',
      team_assignment: 'Team Assignment',
      in_progress: 'In Progress',
      synthesis: 'Synthesis',
      report_draft: 'Report Draft',
      completed: 'Completed',
      closed: 'Closed'
    };
    return map[status] || status;
  };

  const getPriorityColor = (p) => {
    const map = { critical: 'bg-red-600 text-white', high: 'bg-red-100 text-red-800', medium: 'bg-amber-100 text-amber-800', low: 'bg-green-100 text-green-800' };
    return map[p] || 'bg-slate-100 text-slate-800';
  };

  const getWorkloadColor = (wl) => {
    if (wl === 0) return 'text-green-600';
    if (wl <= 2) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className="" data-testid="investigations-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>Investigations</h1>
          <p className="text-slate-500">Approved investigations with team assignment and tracking</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search by number, title, investigator..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-lg" data-testid="search-investigations" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 rounded-lg" data-testid="filter-inv-status">
              <Funnel size={16} className="mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="initiated">Initiated</SelectItem>
              <SelectItem value="team_assignment">Team Assignment</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{summaryCounts.total}</div>
          <div className="text-xs text-slate-500">Total Investigations</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Outfit' }}>{summaryCounts.initiated}</div>
          <div className="text-xs text-slate-500">Awaiting Assignment</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Outfit' }}>{summaryCounts.active}</div>
          <div className="text-xs text-slate-500">Active</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
          <div className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Outfit' }}>{summaryCounts.completed}</div>
          <div className="text-xs text-slate-500">Completed</div>
        </div>
      </div>

      {/* Investigations Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">Loading investigations...</div>
        ) : filteredInvestigations.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            {searchTerm || filterStatus !== 'all' ? 'No investigations match your filters' : 'No investigations yet. Approve an incident notification to create one.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="investigations-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase w-[180px]">Investigation #</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase w-[80px]">Sector</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase w-[170px]">Incident #</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase w-[90px]">Priority</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Lead Investigator</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase w-[120px]">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase w-[100px]">Created</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase w-[110px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvestigations.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`inv-row-${inv.id}`}>
                    <td className="px-4 py-3 font-mono text-sm font-medium text-blue-600 align-middle">{inv.investigation_number}</td>
                    <td className="px-4 py-3 text-center align-middle">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${inv.sector === 'aviation' ? 'bg-blue-100 text-blue-800' : 'bg-cyan-100 text-cyan-800'}`}>
                        {inv.sector === 'aviation' ? <AirplaneTilt size={12} /> : <Anchor size={12} />}
                        {inv.sector === 'aviation' ? 'AVN' : 'MAR'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-sm text-slate-600 align-middle">{inv.incident_number}</td>
                    <td className="px-4 py-3 text-center align-middle">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(inv.priority)}`}>{inv.priority}</span>
                    </td>
                    <td className="px-4 py-3 text-sm align-middle">
                      {inv.lead_investigator ? (
                        <span className="text-slate-900">{inv.lead_investigator}</span>
                      ) : (
                        <span className="text-amber-600 font-medium">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center align-middle">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(inv.status)}`}>{getStatusLabel(inv.status)}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-600 text-center align-middle">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}</td>
                    <td className="px-4 py-3 text-center align-middle">
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0" onClick={() => navigate(`/investigations/${inv.id}`)} data-testid={`view-inv-${inv.id}`}>
                          <Eye size={14} />
                        </Button>
                        {canAssign && (
                          <Button size="sm" className="rounded-lg h-8 px-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white" onClick={() => openAssignDialog(inv)} data-testid={`assign-inv-${inv.id}`}>
                            <UserCirclePlus size={14} className="mr-1" />
                            {inv.lead_investigator ? 'Reassign' : 'Assign'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Assign Dialog */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              Assign Lead Investigator
              {selectedInv && <span className="text-sm font-normal text-slate-500 ml-2">for {selectedInv.investigation_number}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
              <p className="text-sm text-blue-900">
                Investigators are sorted by current workload (least busy first). Select the most suitable investigator based on their availability and specializations.
              </p>
            </div>
            {loadingInvestigators ? (
              <div className="text-center py-8 text-slate-500">Loading investigators...</div>
            ) : investigators.length === 0 ? (
              <div className="text-center py-8 text-slate-400">No available investigators found for this sector.</div>
            ) : (
              <div className="space-y-2">
                {investigators.map((inv) => (
                  <div key={inv.email} className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-all" data-testid={`investigator-option-${inv.email}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900">{inv.name}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600 capitalize">{inv.role}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${inv.sector === 'aviation' ? 'bg-blue-100 text-blue-800' : inv.sector === 'maritime' ? 'bg-cyan-100 text-cyan-800' : 'bg-purple-100 text-purple-800'}`}>
                          {inv.sector}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{inv.email}</div>
                      {inv.specializations?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {inv.specializations.slice(0, 3).map((s) => (
                            <span key={s} className="px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-700 rounded">{s}</span>
                          ))}
                          {inv.specializations.length > 3 && <span className="text-[10px] text-slate-400">+{inv.specializations.length - 3} more</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 ml-4">
                      <div className="text-center">
                        <div className={`text-lg font-bold ${getWorkloadColor(inv.workload)}`} style={{ fontFamily: 'Outfit' }}>{inv.workload}</div>
                        <div className="text-[10px] text-slate-500 uppercase">Workload</div>
                      </div>
                      <div className="text-center text-xs text-slate-500">
                        <div>{inv.active_investigations} inv</div>
                        <div>{inv.active_tasks} tasks</div>
                      </div>
                      <Button size="sm" className="rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white" onClick={() => handleAssign(inv.email)} data-testid={`assign-btn-${inv.email}`}>
                        Assign
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-xl">
          {detailInv && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>{detailInv.investigation_number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-slate-500 uppercase">Sector</span>
                    <div className="text-sm font-medium capitalize">{detailInv.sector}</div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase">Status</span>
                    <div><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusStyle(detailInv.status)}`}>{getStatusLabel(detailInv.status)}</span></div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase">Linked Incident</span>
                    <div className="text-sm font-mono">{detailInv.incident_number}</div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase">Priority</span>
                    <div><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(detailInv.priority)}`}>{detailInv.priority}</span></div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase">Lead Investigator</span>
                    <div className="text-sm font-medium">{detailInv.lead_investigator || <span className="text-amber-600">Unassigned</span>}</div>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500 uppercase">Created</span>
                    <div className="text-sm">{detailInv.created_at ? new Date(detailInv.created_at).toLocaleString() : '-'}</div>
                  </div>
                </div>
                {detailInv.title && (
                  <div>
                    <span className="text-xs text-slate-500 uppercase">Title</span>
                    <div className="text-sm font-medium">{detailInv.title}</div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
